// src/pages/ServiceRequest.js
import React, { useState, useEffect } from 'react';
import { Button, notification, Alert, Breadcrumb } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';

import Application from '../components/Application';
import ComponentSection from '../components/Component';
import JobSection from '../components/JobSection';
import ChannelsSection from '../components/Channels';
import '../design/ServiceRequest.css';

import { fetchComponents } from '../actions/componentActions';
import { getApplicationInformation } from '../actions/applicationactions';
import { buildAllocationYAML } from '../utils/yamlHelpers';
import { downloadYamlFile } from '../utils/downloadYaml';
import { authFetchProxy } from '../authFetch';
import { selectTestbed } from '../slices/testbedSlice';

const ServiceRequest = () => {
  const { applicationId: urlApplicationId } = useParams();
  const [applicationId, setApplicationId] = useState(urlApplicationId || null);

  const [isApplicationSubmitted, setIsApplicationSubmitted] = useState(false);
  const [isComponentsSubmitted, setIsComponentsSubmitted] = useState(false);
  const [isJobsSubmitted, setIsJobsSubmitted] = useState(false);
  const [isChannelsSubmitted, setIsChannelsSubmitted] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const components = useSelector((s) => s.components?.components || []);
  const tbId = useSelector(selectTestbed); // 'tbA' | 'tbB'
  const isTMA = tbId === 'tbB';

  useEffect(() => {
    if (!applicationId) return;
    dispatch(fetchComponents(applicationId)).then(() => {
      setIsApplicationSubmitted(true);
    });
  }, [applicationId, dispatch]);

  useEffect(() => {
    setIsComponentsSubmitted((components?.length || 0) > 0);
    // Debug visibility
    // eslint-disable-next-line no-console
    console.info('[SR] components count:', components?.length);
  }, [components]);

  const canSubmit =
    isApplicationSubmitted &&
    isComponentsSubmitted &&
    isJobsSubmitted &&
    (isTMA ? true : isChannelsSubmitted);

  const handleSubmit = async () => {
  try {
    const appInfo = await dispatch(getApplicationInformation(applicationId)).unwrap();

    const allocationYaml = buildAllocationYAML(appInfo, {
      mode         : isTMA ? 'tma' : 'default',
      cluster_name : isTMA ? 'working-cluster02' : 'kind-qos', 
      node_name    : 'any',
      orchestrator : 'ocm',
    });

    // Optional: keep the download for audit
    downloadYamlFile(appInfo.applicationName || 'application-manifest', allocationYaml);

    notification.success({
       message    : 'Application created',
       description: 'Check application overview for your application details!',
     });

       const res = await authFetchProxy('/jobmanager/groups', {
         method : 'POST',
         headers: {
           'Content-Type': 'application/x-yaml', // text/plain also worked, but x-yaml is clearer
          'Accept'      : 'application/json'
        },
        body: allocationYaml,
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        notification.error({
          message    : 'Submission to job orchestrator failed',
          description: ' please check your VPN.',
          duration   : 8,
        });
        return;
      }

     notification.success({
       message    : 'Application Submitted',
       description: 'Deployment manifest sent to Job-Manager!',
     });

    navigate(`/analysis-results/${applicationId}`, {
      state: {
        applicationName : appInfo.applicationName,
        description     : appInfo.applicationDescription ?? 'a new service request',
        analysisResultId: appInfo.analysisResult?.id ?? null,
        applicationId,
      },
    });
  } catch (err) {
    notification.error({
      message    : 'Submission failed',
      description: err?.message || 'Unexpected error while submitting.',
      duration   : 7,
    });
  }
};


  const showChannelsInfo = isTMA && components.length >= 2;

  return (
<>
    <div className="page-header">
        <div className="page-breadcrumb-wrapper">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">Home</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Service Request</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>
    <div className="service-request p-3 form-container">
      <h3>Service Request</h3>

      {!applicationId ? (
        <Application setApplicationId={setApplicationId} />
      ) : (
        <>
          {/* 1) Create components */}
          <ComponentSection applicationId={applicationId} />

          {/* 2) Jobs (ALWAYS show when at least one component exists) */}
          {components.length >= 1 ? (
            <JobSection
              key={`jobs-${components.length}`}  // force remount when component count changes
              applicationId={applicationId}
              components={components}
              setIsJobsSubmitted={setIsJobsSubmitted}
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="No components yet"
              description="Add a component first to attach jobs."
            />
          )}

          {/* 3) Channels only if 2+ components */}
          {components.length >= 2 && (
            <>
              {showChannelsInfo && (
                <Alert
                  style={{ marginBottom: 12 }}
                  type="info"
                  showIcon
                  message="Channels optional for TMA"
                  description="As a TMA user, you can submit without channels."
                />
              )}
              <ChannelsSection
                key={`channels-${components.length}`}
                applicationId={applicationId}
                components={components}
                setIsChannelsSubmitted={setIsChannelsSubmitted}
                isTMA={isTMA}
              />
            </>
          )}

          {/* 4) Submit / new */}
          <div className="button-container" style={{ marginTop: 16 }}>
            <Button
              onClick={handleSubmit}
              type="primary"
              className="button-primary"
              disabled={!canSubmit}
            >
              Submit All
            </Button>
          </div>

          <div className="button-container">
            <Button
              onClick={() => {
                setApplicationId(null);
                setIsApplicationSubmitted(false);
                setIsComponentsSubmitted(false);
                setIsJobsSubmitted(false);
                setIsChannelsSubmitted(false);
              }}
              type="default"
              className="button-secondary"
            >
              Or Create New Application
            </Button>
          </div>
        </>
      )}
    </div></>
  );
};

export default ServiceRequest;
